import pick from 'lodash/pick';
import omit from 'lodash/omit';
import c from 'classnames';
import { FacetContentProps } from './lego/FacetContent';
import s from './common.scss';

export type FacetSize = 'medium' | 'large';

/**
 * The API that makes a Facet implementation a Facet. Extend this on your implementation's prop interface.
 *
 * These are intended to mirror the options that designers have in figma, though there are a couple additional React
 * specific fields.
 */
export interface CommonFacetAPI {
  size: FacetSize;
  selected?: boolean;
  className?: string;
}

// This typescript hack uses an object that requires a key to match every API field, which we then
// extract via Object.keys.
//
// This adds typescript guide rails that ensures we will always have an up-to-date array of all the CommonFacetAPI
// fields, which we can then use as an argument in `lodash/omit`.
const typescriptHack: { [key in keyof CommonFacetAPI]: undefined } = {
  size: undefined,
  selected: undefined,
  className: undefined,
};
const facetAPIFieldNames = Object.keys(typescriptHack);

/**
 * If a common API field should have a default value, it goes here.
 */
const apiDefaults: Partial<CommonFacetAPI> = {};

/**
 * Each Facet implementation requires the FacetContent component. Separating out the content component's props in each
 * implementation will get unwieldy and is prone to a lot of accidental errors or annoying component differences. So
 * this variable keeps all the consumer-facing Facet API fields we need to pass down in one place.
 */
const apiFieldsToPassToContentComponent = [
  'className',
  'size',
  // This cast converts the array from `string[]` to a union of string literals. Useful for keying into other type objects.
] as const;

/**
 * Each Facet implementation has a root component. This root component could be a <button>, a radio button, whatever.
 * Because of that, there are precious few fields we can pass directly from the consumer-facing Facet API and the root
 * component without some manner of transformation. However, it's possible we'll need to add a consumer-facing API field
 * we can pass through as-is. In the cases we need to do that, we have this variable.
 *
 * Why have this variable at all? Separating out root component props in each implementation will get unwieldy and is
 * prone to a lot of accidental errors or annoying component differences. So this variable keeps all the consumer-facing Facet API
 * fields we need to pass to the root component as-is in one place.
 */
const apiFieldsToPassToRootComponent = [] as const;

/**
 * Picks out all the {@link FacetContentProps} out of the consumer-facing Facet API so that it's easy to pass them along to the
 * FacetContent component.
 */
function getContentComponentProps<T extends CommonFacetAPI>(
  propsWithoutDefaults: T
): FacetContentProps {
  const apiFieldsInProps = pick(
    propsWithoutDefaults,
    apiFieldsToPassToContentComponent
  );
  return { ...apiDefaults, ...apiFieldsInProps };
}

function getRootComponentClassNames<T extends CommonFacetAPI>({
  selected,
  className,
}: T) {
  return c('ds-card', s['root'], selected && 'ds-card-selected', className);
}

/**
 * Picks only the fields from the consumer-facing Facet API that can be passed directly to an implementation's root component.
 *
 * e.g.
 * ```
 *  [...]
 *  const rootComponentProps = getRootComponentProps(props)
 *  return (
 *    <RadioButton {...rootComponentProps}>
 *      <FacetContent {...contentComponentProps}/>
 *    </RadioButton>
 *  )
 * ```
 */
function getRootComponentProps<T extends CommonFacetAPI>(
  propsWithoutDefaults: T
): Pick<T, (typeof apiFieldsToPassToRootComponent)[number]> {
  const props = { ...apiDefaults, ...propsWithoutDefaults };
  return {
    ...pick(props, apiFieldsToPassToRootComponent),
    className: getRootComponentClassNames(props),
  };
}

/**
 * Takes a props type, a props object, and returns a set of variables for easy use inside any Facet implementation.
 */
export function useFacet<P extends CommonFacetAPI>(props: P) {
  const rootComponentProps = getRootComponentProps(props);
  const contentComponentProps = getContentComponentProps(props);
  const implementationSpecificProps = omit(props, [
    ...Object.keys(rootComponentProps),
    ...Object.keys(contentComponentProps),
    ...facetAPIFieldNames,
    'children',
  ]) as Omit<P, keyof CommonFacetAPI>;

  return {
    /**
     * Props that -- in most cases -- should be spread onto the root component of your Facet implementation.
     */
    rootComponentProps,

    /**
     * Props that should be spread onto the content component of your Facet implementation.
     */
    contentComponentProps,

    /**
     * These are props that have nothing to do with the consumer-facing Facet API. They're entirely dependent on your
     * implementation's root component. This is where you'll find radio button props if you use a radio button, button
     * props if you use a `<button>`, etc.
     */
    implementationSpecificProps,
  };
}
